import { useState } from 'react';

import debouncePromise from 'debounce-promise';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { isHTTPError } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { type FormData, type HeartbeatRequest } from '../../common/types';
import { useAsyncValidationMemo } from '../../common/utils/hooks/useAsyncValidationMemo';
import { useHeartbeatsService } from '../../services/heartbeats';
import { heartbeatsResource } from '../../services/resources';
import { useHeartbeatsContext } from '../providers/heartbeats';

import messages from './messages';

export const useFormController = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: heartbeatsData, update } = useResource(heartbeatsResource);
	const { memoize } = useAsyncValidationMemo();
	const [isValidateError, setIsValidateError] = useState(false);
	const { validateLoading, validateName, create, edit } = useHeartbeatsService();
	const { formatMessage } = useIntl();

	const { focusState } = useHeartbeatsContext();

	assert(
		!!heartbeatsData,
		new AssertionError(
			'useFormController',
			'useFormController should not rendered before data is fetched',
		),
	);

	const handleAsyncValidate = () =>
		memoize(async (value?: string) => {
			if (!value || focusState.mode === 'edit') {
				return undefined;
			}

			setIsValidateError(false);
			try {
				await validateHeartbeatName(value);
				return undefined;
			} catch (error: any) {
				setIsValidateError(true);
				if (isHTTPError(error)) {
					return error.message;
				} else {
					return formatMessage(messages.unknownErrorOccurred);
				}
			}
		});

	const validateHeartbeatName = debouncePromise(async (value: any) => {
		await validateName(value);
	}, 1000);

	const handleSubmit = (data: FormData) => {
		switch (focusState.mode) {
			case 'create':
			case 'duplicate':
				return handleCreate(data);
			case 'edit':
				return handleEdit(data);
			default:
				break;
		}
	};

	const handleCreate = (data: FormData) => {
		const request = getRequestFromFormData(data);
		return create(request).then((res) => {
			const newData = { ...heartbeatsData };
			newData.heartbeats?.push(res!);

			update(() => newData);

			focusState.open({ data: res!, mode: 'view' });
		});
	};

	const handleEdit = (data: FormData) => {
		const request = getRequestFromFormData(data);

		return edit(request)
			.then((res) => {
				const newData = { ...heartbeatsData };
				newData.heartbeats.forEach((heartbeat, index) => {
					if (heartbeat.name === res?.name) {
						newData.heartbeats[index] = res;
						return;
					}
				});

				update(() => newData);

				focusState.open({ data: res!, mode: 'view' });
			})
			.then(() => {
				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'heartbeatEdit succeded');
			})
			.catch(() => {
				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'heartbeatEdit failed');
			});
	};

	const getRequestFromFormData = (formData: FormData): HeartbeatRequest => {
		const request: HeartbeatRequest = {
			name: formData.name,
			description: formData.description || '',
			ownerTeamId: formData.assignedTeam.atlassianIdentifier!,
			interval: parseInt(formData.expect.interval?.toString() || ''),
			intervalUnit: formData.expect.unit.value,
			alertPriority: formData.alertMessage.alertPriority,
			alertMessage: formData.alertMessage.alertMessage?.toString() || '',
			alertTags: formData.tags?.length ? formData.tags.map((fieldValue) => fieldValue.value) : [],
		};

		return request;
	};

	return {
		validateLoading,
		isValidateError,
		handleAsyncValidate,
		handleSubmit,
	};
};
